import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useSearchContext } from '../context/SearchContext';
import { SearchResultsProps } from '../types';
import CardsContainer from './CardsContainer';
import {
  handleChangePage,
  handleGetNeighborAreas
} from '../../../helpers/search-filters';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import ArrowSwitchIcon from '../../../new-components/svg-icons/ArrowSwitchIcon';
import { SearchFiltersActionTypes } from '../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../reducers/search-filters/reducer';
import { updateQuery } from '../../../helpers/common';
import { toast } from 'react-toastify';
import { logEvent } from '../../../new-lib/analytics';
import { EntityTypes } from '../../../new-lib/constants/common';
import ViewOnMapBtn from './ViewOnMapBtn';
import { isEqual } from '../../../helpers/isEqual';
import RemoveFilterNote from '../../../new-components/RemoveFilterNote';

const SearchResults = ({ customized }: SearchResultsProps) => {
  const router = useRouter();
  const { locale } = router;
  const { t } = useTranslation('search');
  const cardsContainerRef = useRef<any>(null);
  const containerRef = useRef<any>(null);
  const searchBodyResults = useSelector(
    (state: any) => state.searchResults.searchResults,
    isEqual
  );
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { searchFiltersState, searchFiltersDispatch, showFilters } =
    useSearchContext();
  const [allSearchResults, setAllSearchResults] = useState<any[]>(
    searchBodyResults.loadedSearchResults
  );
  const [neighboringResults, setNeighboringResults] = useState<any[]>([]);
  const [neighboringPage, setNeighboringPage] = useState<number>(0);
  const [toaseterShowed, setToaseterShowed] = useState<boolean>(false);
  const fetchingData = useRef<boolean>(false);
  const ResultsFinished = useRef<boolean>(false);
  const maxPageNumber = useRef<number>(
    Math.ceil(searchBodyResults.loadedSearchResultsCount / 12)
  );
  const neighboringResultsFetched = useRef<boolean>(false);
  const handleScroll = async (scrollPos: number, heightPos: number) => {
    if (scrollPos >= heightPos && !fetchingData.current) {
      fetchingData.current = true;
      try {
        if (
          searchFiltersState.page_number &&
          searchFiltersState.page_number >= maxPageNumber.current &&
          !ResultsFinished.current
        ) {
          const neighborResults = await handleGetNeighborAreas(
            searchFiltersState,
            neighboringPage,
            undefined,
            undefined,
            locale
          ).then(res => res?.data.values);
          if (neighborResults.length > 0) {
            setNeighboringResults(neighborResults);
            setNeighboringPage(neighboringPage + 1);
            if (neighborResults.length < 12) {
              ResultsFinished.current = true;
            }
          } else {
            ResultsFinished.current = true;
          }
        } else if (
          searchFiltersState.page_number &&
          searchFiltersState.page_number < maxPageNumber.current
        ) {
          handleChangePage(allSearchResults.length, searchFiltersDispatch);
        }
      } catch (error) {
        return;
      } finally {
        fetchingData.current = false;
      }
    }
  };

  const getBtnText = () => {
    if (searchFiltersState.searchFilters.category == 'property') {
      return `${t('switch_to')} ${t('compounds')}`;
    } else {
      return `${t('switch_to')} ${t('properties')}`;
    }
  };

  const handleCategoryToggle = () => {
    const value =
      searchFiltersState.searchFilters.category == 'property'
        ? 'compound'
        : 'property';

    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_SORTING_ORDER,
      payload: {
        order_by: '',
        order_direction: ''
      }
    });
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE,
      payload: {
        name: SearchFiltersTypes.CATEGORY,
        value: value
      }
    });

    updateQuery(
      router,
      [{ name: SearchFiltersTypes.CATEGORY, value }],
      ['sort_by']
    );

    logEvent.click('search_switch', 'search_page', 'toggle_btn', {
      entity_type:
        value == 'property' ? EntityTypes.PROPERTY : EntityTypes.COMPOUND
    });
  };

  useEffect(() => {
    if (searchBodyResults.loadedSearchResults) {
      if (
        searchFiltersState.page_number != 1 &&
        searchFiltersState.page_number != undefined
      ) {
        setAllSearchResults([
          ...allSearchResults,
          ...searchBodyResults.loadedSearchResults
        ]);
      } else {
        setAllSearchResults([]);
        setAllSearchResults([...searchBodyResults.loadedSearchResults]);
      }
    }

    if (searchBodyResults.loadedSearchResultsCount) {
      maxPageNumber.current = Math.ceil(
        searchBodyResults.loadedSearchResultsCount / 12
      );
    }
  }, [searchBodyResults.loadedSearchResults]);
  useEffect(() => {
    const areas = router.query['areas']?.toString().split(',').map(Number);
    const property_types = router.query['property_types']
      ?.toString()
      .split(',')
      .map(Number);
    if (
      searchBodyResults.loadedSearchResultsCount <= 2 &&
      !isMobile &&
      !neighboringResultsFetched.current
    ) {
      try {
        if (searchFiltersState?.searchFilters.areas.length > 0) {
          neighboringResultsFetched.current = true;
          handleGetNeighborAreas(
            searchFiltersState,
            neighboringPage,
            areas,
            property_types,
            locale
          ).then((res: any) => {
            setNeighboringResults(res?.data.values);
            setNeighboringPage(neighboringPage + 1);
          });
        }
      } catch (error) {
        return;
      }
    }
  }, [searchBodyResults.loadedSearchResultsCount]);

  useEffect(() => {
    const handleScrollEvent = () => {
      if (isMobile) {
        handleScroll(
          window.scrollY + window.innerHeight + 200,
          cardsContainerRef?.current?.offsetHeight
        );
      } else {
        handleScroll(
          containerRef.current.scrollTop +
            containerRef.current.offsetHeight +
            200,
          cardsContainerRef?.current?.offsetHeight
        );
      }
    };

    if (cardsContainerRef.current && containerRef.current) {
      if (isMobile) {
        window.addEventListener('scroll', handleScrollEvent);
      } else {
        containerRef.current.addEventListener('scroll', handleScrollEvent);
      }
    }
    return () => {
      if (cardsContainerRef.current && containerRef.current) {
        if (isMobile) {
          window.removeEventListener('scroll', handleScrollEvent);
        } else {
          containerRef.current.removeEventListener('scroll', handleScrollEvent);
        }
      }
    };
  }, [searchBodyResults.loadedSearchResults, allSearchResults]);

  useEffect(() => {
    if (
      neighboringResults?.length > 0 &&
      !router.asPath.includes('/affordability-results')
    ) {
      setAllSearchResults([...allSearchResults, ...neighboringResults]);
      if (neighboringPage <= 1 && !toaseterShowed) {
        logEvent.search(
          'Neighboring_area_scroll',
          'search_page',
          'search',
          searchFiltersState,
          {
            area_id: searchFiltersState.searchFilters.areas[0],
            area_name: allSearchResults[0]?.area.name
          }
        );
        setTimeout(() => {
          toast.info(
            `${t('you_are_now_browsing')} ${
              searchFiltersState.searchFilters.category == 'property'
                ? t('properties').toLowerCase()
                : t('compounds').toLowerCase()
            } ${t('in_nearby_areas')}`,
            {
              position: 'bottom-center',
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: false,
              draggable: false,
              progress: undefined,
              theme: 'dark',
              bodyClassName: 'toast-body',
              className: 'neighboring-toaster',
              style: {
                bottom: isMobile ? '80px' : '40px',
                margin: '0 auto'
              },
              icon: false
            }
          );
        }, 1000);
        setToaseterShowed(true);
      }
    }
  }, [neighboringResults]);

  useEffect(() => {
    if (
      searchFiltersState.page_number &&
      searchFiltersState.page_number == 1 &&
      containerRef.current
    ) {
      containerRef?.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [searchFiltersState]);

  return (
    <Container ref={containerRef}>
      <RemoveFilterNote />
      <div className="cards-container" ref={cardsContainerRef}>
        <CardsContainer searchResults={allSearchResults} />
      </div>
      {isMobile && !showFilters && !customized && (
        <ActionButtonsWrapper>
          <div className="btns-container">
            <ViewOnMapBtn />
            <div className="separator"></div>
            <button className="switch-btn" onClick={handleCategoryToggle}>
              <ArrowSwitchIcon stroke="#fff" /> {getBtnText()}
            </button>
          </div>
        </ActionButtonsWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  scrollbar-width: none;
  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    width: 100%;
    position: initial;
  }
`;

const ActionButtonsWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    #fff 34.5%,
    #fff 100%
  );

  padding: 4px;
  width: 100vw;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  .btns-container {
    display: flex;
    justify-content: center;
    flex-direction: row;
    padding: 12px 14px;
    border-radius: 360px;
    background-color: #015c9a;
    border: 4px solid #fff;
    box-shadow: 0 2px 4px 4px rgba(0, 0, 0, 0.125);
    align-items: center;
    width: 334px;
    gap: 4px;
    .separator {
      width: 1px;
      height: 30px;
      background-color: #fff;
    }
    button {
      background-color: transparent;
      border: none;
      outline: none;
      color: #fff;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
      font-family: materialMedium;

      svg {
        path {
          stroke: #fff;
        }
      }
    }
  }
`;

export default SearchResults;
